import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Fade,
  Paper,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PanToolIcon from "@mui/icons-material/PanTool";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useTranslation } from "react-i18next";
import logger from "../../utils/logger";

export type TakeOverFieldProps = {
  onSubmit: (reason: string) => void;
  onCancel: () => void;
};

const TakeOverField: React.FC<TakeOverFieldProps> = ({
  onSubmit,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [reason, setReason] = useState<string>("");
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [step, setStep] = useState<"input" | "confirm">("input");
  const isValid = reason.trim().length > 0;

  const handleNext = () => {
    if (!isValid) return;
    setStep("confirm");
  };

  const handleConfirm = async () => {
    if (!isValid) return;
    setIsSubmitting(true);
    try {
      await fetch("/api/takeover", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason: reason.trim() }),
      });
      onSubmit(reason.trim());
    } catch (err) {
      logger.error(t("takeOverError"), err);
    } finally {
      setIsSubmitting(false);
    }
  };
  {
    /* header */
  }
  return (
    <Fade in timeout={300}>
      <Paper
        elevation={0}
        sx={{
          background: "linear-gradient(to right bottom, #EFF6FF, #DBEAFE)",
          borderRadius: "0.5em",
          border: "0.0625em solid #DBEAFE",
          mb: "1.5em",
          overflow: "hidden",
        }}
      >
        {step === "input" ? (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "0.75em",
                p: "1em",
                pb: "0.75em",
              }}
            >
              <Box
                sx={{
                  width: "1.75em",
                  height: "1.75em",
                  borderRadius: "50%",
                  backgroundColor: "rgba(59,130,246,0.15)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <PanToolIcon sx={{ color: "#3B82F6", fontSize: "1.15em" }} />
              </Box>
              <Typography
                variant="subtitle2"
                fontWeight={600}
                sx={{ color: "#1E40AF", fontSize: "0.9em" }}
              >
                {t("takeOverPrompt")}
              </Typography>
              <IconButton
                size="small"
                onClick={onCancel}
                sx={{ ml: "auto", color: "#1E40AF" }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            <Typography
              variant="body2"
              sx={{ px: "1.5em", pb: "0.75em", color: "#1E40AF", ml: "4.5em" }}
            >
              {t("takeOverHelper")}
            </Typography>

            <Box sx={{ px: "1.5em", pb: "0.75em" }}>
              <TextField
                fullWidth
                multiline
                minRows={1}
                maxRows={3}
                placeholder={t("takeOverPlaceholder")}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#F0F9FF",
                    borderRadius: "0.5em",
                    transition: "all 0.3s ease",
                    fontSize: "0.875em",
                    "&.Mui-focused": {
                      boxShadow: "0 0 0 0.125em rgba(59,130,246,0.2)",
                    },
                    "& fieldset": {
                      borderColor: isFocused ? "#3B82F6" : "#DBEAFE",
                    },
                    "&:hover fieldset": {
                      borderColor: "#3B82F6",
                    },
                  },
                }}
              />
            </Box>

            <Box
              sx={{
                px: "1.5em",
                pb: "1.25em",
                display: "flex",
                justifyContent: "flex-end",
                gap: "0.75em",
              }}
            >
              <Button
                variant="outlined"
                onClick={onCancel}
                sx={{
                  textTransform: "none",
                  borderColor: "#3B82F6",
                  color: "#1E40AF",
                  fontSize: "0.875em",
                }}
              >
                {t("cancel")}
              </Button>
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={!isValid}
                endIcon={<CheckCircleOutlineIcon />}
                sx={{
                  textTransform: "none",
                  backgroundColor: "#3B82F6",
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: "0.875em",
                  "&:hover": { backgroundColor: "#2563EB" },
                }}
              >
                {t("next")}
              </Button>
            </Box>
          </>
        ) : (
          <>
            <Typography
              sx={{ p: "1.5em", fontSize: "0.9em" }}
              color="text.primary"
            >
              {t("takeOverConfirmMessage")}
            </Typography>
            <Box
              sx={{
                px: "1.5em",
                pb: "1.25em",
                display: "flex",
                justifyContent: "flex-end",
                gap: "0.75em",
              }}
            >
              <Button
                variant="outlined"
                onClick={onCancel}
                sx={{
                  textTransform: "none",
                  borderColor: "#3B82F6",
                  color: "#1E40AF",
                  fontSize: "0.875em",
                }}
              >
                {t("cancel")}
              </Button>
              <Button
                variant="contained"
                onClick={handleConfirm}
                disabled={!isValid || isSubmitting}
                endIcon={
                  isSubmitting ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <CheckCircleOutlineIcon />
                  )
                }
                sx={{
                  textTransform: "none",
                  color: "#fff",
                  backgroundColor: "#DC2626",
                  fontSize: "0.875em",
                  "&:hover": { backgroundColor: "#B91C1C" },
                }}
              >
                {t("confirm")}
              </Button>
            </Box>
          </>
        )}
      </Paper>
    </Fade>
  );
};

export default TakeOverField;
